// src/components/ajuda/CentralAjuda.js
// Painel lateral (gaveta) com busca de dúvidas e acesso ao tour.
// A busca não usa IA: compara as palavras digitadas com as perguntas e
// palavras-chave de ajudaConteudo.js, ignorando acentos e palavras comuns.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, PlayCircle, Search, X, RotateCcw } from "lucide-react";
import { NOMES_PAGINAS, PALAVRAS_IGNORADAS, PERGUNTAS, ROTAS, TOURS } from "./ajudaConteudo";
import { normalizarTexto } from "../../utils/tikpontos";
import "./Ajuda.css";

function quebrarPalavras(texto, { ignorarComuns = false } = {}) {
  return normalizarTexto(texto)
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .filter((p) => !ignorarComuns || !PALAVRAS_IGNORADAS.has(p))
    .map((p) => (p.length > 4 && p.endsWith("s") ? p.slice(0, -1) : p));
}

const INDICE = PERGUNTAS.map((item) => ({
  ...item,
  palavras: quebrarPalavras(
    `${item.pergunta} ${item.chaves} ${NOMES_PAGINAS[item.rota] || ""}`
  ),
}));

function parecidas(termo, palavra) {
  if (termo.length < 3) return false;
  if (palavra.startsWith(termo)) return true;
  // mesma raiz: "vencem" ~ "vencer", "cadastrei" ~ "cadastrar"
  return termo.length >= 5 && palavra.length >= 5 && termo.slice(0, 5) === palavra.slice(0, 5);
}

export function buscarPerguntas(consulta) {
  const termos = quebrarPalavras(consulta, { ignorarComuns: true }).filter((t) => t.length >= 2);
  if (!termos.length) return { termos, resultados: [] };

  // Palavras que aparecem em muitas perguntas (ex.: "pontos") valem menos
  const peso = (termo) => {
    const ocorrencias = INDICE.filter((i) => i.palavras.some((p) => p === termo || parecidas(termo, p))).length;
    return ocorrencias > 4 ? 0.5 : 1;
  };

  const resultados = INDICE.map((item) => {
    let pontos = 0;
    termos.forEach((termo) => {
      if (item.palavras.includes(termo)) pontos += 3 * peso(termo);
      else if (item.palavras.some((p) => parecidas(termo, p))) pontos += 2 * peso(termo);
    });
    return { item, pontos };
  })
    .filter((r) => r.pontos > 0)
    .sort((a, b) => b.pontos - a.pontos || Number(!!b.item.destaque) - Number(!!a.item.destaque))
    .slice(0, 6)
    .map((r) => r.item);

  return { termos, resultados };
}

export default function CentralAjuda({ aberto, onFechar, rotaAtual, onIrPara, onIniciarTour }) {
  const [consulta, setConsulta] = useState("");
  const inputRef = useRef(null);
  const focoAnterior = useRef(null);

  useEffect(() => {
    if (!aberto) return;
    focoAnterior.current = document.activeElement;
    setConsulta("");
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    const onKey = (e) => {
      if (e.key === "Escape") onFechar?.();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      focoAnterior.current?.focus?.();
    };
  }, [aberto, onFechar]);

  const { termos, resultados } = useMemo(() => buscarPerguntas(consulta), [consulta]);
  const lista = termos.length ? resultados : PERGUNTAS.filter((p) => p.destaque);
  const semResultado = termos.length > 0 && resultados.length === 0;

  const temTourDaPagina = rotaAtual !== ROTAS.home && Boolean(TOURS[rotaAtual]);

  if (!aberto) return null;

  return createPortal(
    <div className="aj-central">
      <button
        type="button"
        className="aj-central-fundo"
        aria-label="Fechar ajuda"
        onClick={onFechar}
      />
      <aside className="aj-central-painel" role="dialog" aria-modal="true" aria-labelledby="aj-central-titulo">
        <div className="aj-central-topo">
          <h2 id="aj-central-titulo">Como podemos ajudar?</h2>
          <button type="button" className="aj-icone-btn" aria-label="Fechar" onClick={onFechar}>
            <X size={20} />
          </button>
        </div>

        <label className="aj-busca">
          <span className="aj-busca-rotulo">Digite sua dúvida</span>
          <span className="aj-busca-campo">
            <Search size={19} aria-hidden="true" />
            <input
              ref={inputRef}
              type="search"
              value={consulta}
              onChange={(e) => setConsulta(e.target.value)}
              placeholder="Ex.: como troco meus pontos?"
              autoComplete="off"
            />
          </span>
        </label>

        <div className="aj-central-lista">
          <span className="aj-central-secao">
            {termos.length ? "Resultados" : "Dúvidas mais comuns"}
          </span>

          {lista.map((item) => (
            <div key={item.pergunta} className="aj-resultado">
              <button
                type="button"
                className="aj-resultado-principal"
                onClick={() => onIrPara?.(item.rota)}
              >
                <span className="aj-resultado-textos">
                  <span className="aj-resultado-pergunta">{item.pergunta}</span>
                  <span className="aj-resultado-destino">
                    Ir para <strong>{NOMES_PAGINAS[item.rota] || "a página"}</strong>
                  </span>
                </span>
                <ArrowRight size={18} aria-hidden="true" />
              </button>
              {TOURS[item.rota] && (
                <button
                  type="button"
                  className="aj-resultado-tour"
                  onClick={() => onIniciarTour?.(item.rota)}
                >
                  <PlayCircle size={15} aria-hidden="true" />
                  Me mostre como
                </button>
              )}
            </div>
          ))}

          {semResultado && (
            <div className="aj-central-vazio">
              <strong>Não encontramos nada sobre isso.</strong>
              <span>
                Tente outras palavras ou escreva para{" "}
                <a href="mailto:sac@tikpag.com.br">sac@tikpag.com.br</a>.
              </span>
            </div>
          )}
        </div>

        <div className="aj-central-rodape">
          {temTourDaPagina && (
            <button
              type="button"
              className="tk-btn tk-btn--secundario tk-btn--bloco"
              onClick={() => onIniciarTour?.(rotaAtual)}
            >
              <PlayCircle size={18} aria-hidden="true" />
              Tour desta página
            </button>
          )}
          <button
            type="button"
            className="tk-btn tk-btn--escuro tk-btn--bloco"
            onClick={() => onIniciarTour?.(ROTAS.home)}
          >
            <RotateCcw size={18} aria-hidden="true" />
            {rotaAtual === ROTAS.home ? "Refazer o tour guiado" : "Tour geral do painel"}
          </button>
        </div>
      </aside>
    </div>,
    document.body
  );
}
